import { useCallback } from "react";
import { z } from "zod";
import { pluralizeWithCount } from "@common/helpers/string";
import type { TagsInputProps } from "@design-system/TagsInput/TagsInput";
import { useToaster } from "@design-system/ToastMessages/useToaster";

const emailSchema = z.string().email();

const isValidEmail = (value: string) => emailSchema.safeParse(value).success;

const toLowerCase = (value: string) => value.toLowerCase();

/**
 * The email rules every email `TagsInput` shares: lowercase each entry,
 * accept only valid addresses, and toast the rejected ones. Spread the result
 * onto `TagsInput`; `fields.Emails` applies it for kit forms.
 */
export const useEmailTagsRules = (): Pick<
    TagsInputProps,
    "normalizeValue" | "isValidValue" | "onInvalidValues"
> => {
    const { error: errorToast } = useToaster();
    const onInvalidValues = useCallback(
        (invalidEmails: string[]) => {
            errorToast(
                invalidEmails.length === 1
                    ? `"${invalidEmails[0]}" is not a valid email address.`
                    : `${pluralizeWithCount("invalid email", invalidEmails.length)} skipped: ${invalidEmails.join(", ")}`,
            );
        },
        [errorToast],
    );

    return {
        normalizeValue: toLowerCase,
        isValidValue: isValidEmail,
        onInvalidValues,
    };
};
